import React from "react";
import { Col, Row } from "react-bootstrap";
import { useQuery } from "react-query";
import { useHistory, useParams } from "react-router-dom";

import { TextWithLoader, EmptyBlock } from "components";
import { LineDivider, ViewContainer } from "components/layout";
import { getFormattedDate, hasPermissions, permissionList } from "helpers";
import { getScheduleAFriendlyId } from "mappers";
import { scheduleAStatuses } from "models";
import { scheduleAService } from "services";

import HistoryScheduleA from "./components/history";
import { FeesViewBlock } from "./feesViewBlock";

type ScheduleAViewProps = {
	userId: string;
	scheduleId: string;
	isShowHistorySection?: boolean;
	scheduleAName?: string;
	onError?: any;
};

export const ScheduleAView = ({
	userId,
	scheduleId,
	scheduleAName,
	isShowHistorySection = false,
	onError,
}: ScheduleAViewProps) => {
	const history = useHistory();
	const params: { id: string; scheduleId: string } = useParams();

	const { data, isLoading, isError } = useQuery(
		["viewScheduleAInfo", scheduleId],
		() => scheduleAService.getScheduleA({ userId, scheduleId }),
		{
			retry(failureCount, error) {
				if (failureCount < 3) {
					if (error && String(error).indexOf("403") !== -1) {
						return false;
					}
					return true;
				} else {
					history.push(
						`/system-setup/agent-setup/edit-agent/${params.id}/schedule-a/${params.scheduleId}/error`,
					);
					return false;
				}
			},
			onError,
		},
	);

	const status = scheduleAStatuses.find(
		({ value }) => value === data?.status,
	);

	const createScheduleAOrResidualSplitProfileForAgentOfficeOrAgent =
		hasPermissions(
			permissionList.CreateScheduleAOrResidualSplitProfileForAgentOfficeOrAgent,
		);

	const editExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent =
		hasPermissions(
			permissionList.EditExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent,
		);

	return (
		<>
			<ViewContainer>
				{isError ? (
					<EmptyBlock />
				) : (
					<>
						{scheduleAName && (
							<Row>
								<Col className="mb-4 mt-2">
									<TextWithLoader
										isHideLabel={true}
										value={scheduleAName}
										loading={isLoading}
										labelCol={5}
										valueCol={7}
									/>
								</Col>
							</Row>
						)}
						{data?.is_default && (
							<Row>
								<Col>
									<TextWithLoader
										value="Default Schedule A"
										loading={isLoading}
										isIconCheck
										isHideLabel
									/>
								</Col>
							</Row>
						)}
						<Row>
							<Col>
								<TextWithLoader
									label="Schedule A ID:"
									value={getScheduleAFriendlyId(data)}
									loading={isLoading}
									labelCol={5}
									valueCol={7}
								/>
							</Col>
							<Col>
								<TextWithLoader
									label="Created at:"
									value={getFormattedDate({
										date: data?.created_at,
										format: "DD MMMM YYYY",
										keepLocalTime: true,
									})}
									loading={isLoading}
									labelCol={5}
									valueCol={7}
								/>
							</Col>
						</Row>
						<Row>
							<Col>
								<TextWithLoader
									label="Schedule A Name:"
									value={data?.name}
									loading={isLoading}
									labelCol={5}
									valueCol={7}
									className="break-word"
								/>
							</Col>
							<Col>
								<TextWithLoader
									label="Processor:"
									value={data?.processor?.name}
									loading={isLoading}
									labelCol={5}
									valueCol={7}
								/>
							</Col>
						</Row>
						<Row>
							<Col>
								<TextWithLoader
									label="Status:"
									value={status?.label}
									loading={isLoading}
									labelCol={5}
									valueCol={7}
								/>
							</Col>
							<Col>
								<TextWithLoader
									label={
										data?.are_fees_not_applicable
											? "Buy Rates are not applicable"
											: "Buy Rates are applicable"
									}
									loadingLabel={isLoading}
									labelCol={12}
								/>
							</Col>
						</Row>
						<Row>
							<Col>
								<TextWithLoader
									label="Copy from Agent Office Schedule A`s?"
									value={data?.parent?.name}
									loading={isLoading}
									labelCol={5}
									valueCol={7}
								/>
							</Col>
							<Col></Col>
						</Row>
						<LineDivider />
						{!data?.are_fees_not_applicable && !isLoading && (
							<FeesViewBlock
								feesData={data}
								isLoading={isLoading}
							/>
						)}
					</>
				)}
			</ViewContainer>
			{isShowHistorySection && (
				<>
					{createScheduleAOrResidualSplitProfileForAgentOfficeOrAgent &&
						editExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent && (
							<ViewContainer>
								<HistoryScheduleA scheduleId={scheduleId} />
							</ViewContainer>
						)}
				</>
			)}
		</>
	);
};
